import React from 'react';
import { forEach, map } from 'lodash/collection';
import { isArray, isEmpty } from 'lodash/lang';

import ModalBoxes from '../../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import NotificationManager from '../../../../../../../../common/notifications/NotificationManager';
import { FINAL_OPTION_LIST_REQUIRED } from '../../../../../../../../constants/notificationMessages';
import { useCurrentRoute } from '../../../../../../../root/router';
import {
  getFieldLabel,
  optionUsedInConditionalLogicModalConfirmation
} from '../../../../../services/conditionalLogicUtils';
import EditFormAnswerFields from '../../../EditFormAnswerFields';

import './EditFormAnswerView.scss';

export function EditFormAnswerView({
  isExtensible,
  updateInputsAndPreviewData,
  item,
  labelIndex,
  index,
  inputs,
  editMode,
  terminologyVersionGroupNumber,
  custom
}) {
  const {
    params: { protocolIdentity },
    name
  } = useCurrentRoute();
  switch (isExtensible) {
    case 1:
      return (
        <i
          className="glyphicon glyphicon-edit pr-1 pb-2 editicn editAnsSetup"
          title="Edit Controlled Term List"
          onClick={() => onEditFormAnswerFields(item, labelIndex, index, custom ? 'custom' : 'optional')}
        />
      );
    case 2:
      return (
        <i
          className="glyphicon glyphicon-cog pr-1 editicn editAns"
          title="Custom Options List"
          onClick={() => onEditFormAnswerFields(item, labelIndex, index, custom ? 'custom' : 'optional')}
        />
      );
    case 3:
      return (
        <i
          className="glyphicon glyphicon-check pr-1 editicn editAns"
          title="Subset Options List"
          onClick={() => onEditFormAnswerFields(item, labelIndex, index, 'optional')}
        />
      );
    default:
      return null;
  }
  function onEditFormAnswerFields(item, labelIndex, questionIndex, type) {
    const modalBox = ModalBoxes.open({
      component: (
        <EditFormAnswerFields
          pageName={name}
          terminologyVersionGroupNumber={terminologyVersionGroupNumber}
          checkAndPromptLinkedFieldsBeforeDelete={function(optionName) {
            return checkAndPromptLinkedFieldsBeforeDeleteForLegacyIg(inputs, item.name, optionName);
          }}
          onSave={function(data) {
            if (!data) {
              return;
            }
            const { finalOptions } = data;

            if (isEmpty(finalOptions)) {
              return NotificationManager.error(FINAL_OPTION_LIST_REQUIRED);
            }

            const { inputType, codeDefinationList, name } = item;

            cleanupConditionsLinkedToDeletedOptions(inputs, finalOptions, codeDefinationList, name);

            const itemDefinitionPath = type === 'custom' ? 'customItemDefinationList' : 'itemDefinitionList';
            const field = inputs[0][type][labelIndex][itemDefinitionPath][questionIndex];
            field.codeDefinationList = prepareNextCodeDefinitionList(finalOptions, inputType);
            field.ctListName = data.selectedControlTermListItem;

            if (type !== 'custom') {
              const foundCustom = finalOptions.filter(k => {
                return k.type === 'custom';
              });
              let editedFoundStandard = finalOptions.filter(k => {
                return !k.hasOwnProperty('type');
              });
              inputs[0][type][labelIndex].itemDefinitionList[questionIndex].customOptionList = foundCustom;
              inputs[0][type][labelIndex].itemDefinitionList[questionIndex].editedFoundStandard = editedFoundStandard;
            }

            updateInputsAndPreviewData(inputs, () => {
              ModalBoxes.close(modalBox.id);
            });
          }}
          hideModal={() => {
            ModalBoxes.close(modalBox.id);
          }}
          itemDefinationData={item}
          fromEdit={editMode}
          protocolId={protocolIdentity}
        />
      )
    });
  }
}

function prepareNextCodeDefinitionList(finalOptions, inputType) {
  return finalOptions.map(function(element) {
    const tempCodeDefObj = {
      codeId: 0,
      codeOid: '',
      codedValue: null,
      decode: element.value,
      id: crypto.randomUUID(),
      inputType: ['dropdown', 'dropdownCT'].includes(inputType) ? 'dropdown' : 'multiselect',
      name: element.value,
      sequence: element.sequence
    };

    if (element.hasOwnProperty('type') && element.type === 'custom') {
      tempCodeDefObj.type = element.type;
    }
    return tempCodeDefObj;
  });
}

function cleanupConditionsLinkedToDeletedOptions(inputs, finalOptions, codeDefinitionList, optionFieldName) {
  const originOptions = map(codeDefinitionList, 'decode');
  const nextOptions = map(finalOptions, 'value');
  const removedOptions = originOptions.filter(o => !nextOptions.includes(o));
  if (isEmpty(removedOptions)) {
    return;
  }
  removeLinkedOptionsFromConditions(inputs, optionFieldName, removedOptions);
}

export function removeLinkedOptionsFromConditions(inputs, optionFieldName, options) {
  throughAllFieldsForLegacyIg(inputs, function(field) {
    throughFieldCondition(field, function(item, index) {
      const { conditionFieldName, conditionFieldAnswerNames } = item;
      if (conditionFieldName === optionFieldName) {
        const nextConditionFieldAnswerNames = conditionFieldAnswerNames.filter(name => !options.includes(name));
        if (nextConditionFieldAnswerNames.length === conditionFieldAnswerNames.length) {
          return;
        }
        if (isEmpty(nextConditionFieldAnswerNames)) {
          if (isArray(field.fieldConfigurationResponse)) {
            field.fieldConfigurationResponse.splice(index, 1);
            return;
          }
          delete field.fieldConfigurationResponse;
          return;
        }
        item.conditionFieldAnswerNames = nextConditionFieldAnswerNames;
      }
    });
  });
}

export function checkAndPromptLinkedFieldsBeforeDeleteForLegacyIg(inputs, optionFieldName, optionName) {
  const fieldsLinkedByOption = [];

  throughAllFieldsForLegacyIg(inputs, function(field) {
    throughFieldCondition(field, function({ conditionFieldName, conditionFieldAnswerNames }) {
      if (conditionFieldName === optionFieldName && conditionFieldAnswerNames.includes(optionName)) {
        fieldsLinkedByOption.push(getFieldLabel(field));
      }
    });
  });

  return promptLinkedFieldsBeforeDelete(fieldsLinkedByOption, optionName);
}

function promptLinkedFieldsBeforeDelete(fieldsLinkedByOption, optionName) {
  if (isEmpty(fieldsLinkedByOption)) {
    return Promise.resolve();
  }

  return optionUsedInConditionalLogicModalConfirmation(fieldsLinkedByOption, optionName);
}

function throughFieldCondition(field, callback) {
  const configuration = field.fieldConfigurationResponse;
  if (!configuration) {
    return;
  }
  callback(configuration);
}

function throughAllFieldsForLegacyIg(inputs, callback) {
  const { custom, optional } = inputs[0];
  forEach({ custom, optional }, function(fieldSet, key) {
    fieldSet.forEach(function(fieldGroup) {
      const definitionList = fieldGroup[key === 'custom' ? 'customItemDefinationList' : 'itemDefinitionList'];
      definitionList.forEach(callback);
    });
  });
}
